import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a pincode checker.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} pincodeChecker – The pincode-checker element.
 *
 * @extends Component<Refs>
 */
export class PincodeChecker extends Component {
    //requiredRefs = ['pincodeChecker'];

    constructor() {
        super();
        this.variant = this?.dataset?.variant ? JSON.parse(this.dataset.variant) : null;
        this.pincodeData = typeof this.querySelector('script') !== 'undefined' && this?.querySelector('script')?.textContent ? JSON.parse(String(this.querySelector('script')?.textContent)) : null;
        this.responseError = this.dataset.responseError ?? '';
        this.deliveryInfoTemplate = this.querySelector('template')?.innerHTML ?? null;
        this.resultContainer = this.querySelector('[data-response]');
        this.submitButton = this.querySelector('button');
        this.pincodeInput = this.querySelector('input');
        this.atcButton = this.closest('form')?.querySelectorAll('.add-to-cart-button');
        this.stickyAtcButton = document.querySelectorAll('.sticky-add-to-cart__button');
        this.buyButton = this.closest('form')?.querySelector('.accelerated-checkout-block');
    }

    /** @type {AbortController} */
    #abortController = new AbortController();

    /**
     * Handles variant update.
     * @param {CustomEvent} event
     * 
     */
    #onVariantUpdate = (event) => {
        //console.log(event.detail);
        this.variant = event.detail.resource;
        this.togglePincodeChecker();
        this.checkPincode();
    }

    connectedCallback() {
        super.connectedCallback();
        this?.querySelector('button')?.addEventListener('click', this.checkPincode);

        //initialize pincode check if pincode is available in session storage
        if(this.retrievePincode() && this.pincodeInput) {
            this.pincodeInput.value = String(this.retrievePincode());
        }

        //check pincode on page refresh
        this.checkPincode();

        const { signal } = this.#abortController;
        const target = this.closest('.shopify-section, dialog, product-card');
        target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
        //target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantUpdate, { signal });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
        this.removePincode()
    }

    /**
     * Handles storing pincode.
     * @param {string} pincode
     * 
     */
    storePincode = (pincode) => {
        if(typeof pincode === 'string'){
            sessionStorage.setItem("customerPincode", pincode);
        } 
    }

    /**
     * Handles removing pincode.
     * 
     */
    removePincode = () => {
        sessionStorage.removeItem("customerPincode");
    }

     /**
     * Handles retrieving pincode.
     * 
     * @returns string
     */
    retrievePincode = () => {
        return sessionStorage.getItem("customerPincode");
    }

    /**
     * Handles pincode checker display.
     */
    togglePincodeChecker = () => {
        this.style.display = this.variant.available ? 'block' : 'none';
    }

    /**
     * Handles pincode check.
     */
    checkPincode = () => {
        if(
            !this.pincodeInput?.value || 
            !this.submitButton || 
            !this.pincodeData?.length || 
            !this.deliveryInfoTemplate || 
            !this.resultContainer
        ) return;
        this.submitButton.classList.add('loading');
        const currentPincode = this.pincodeInput.value.trim();
        const resultContainer = this.resultContainer;
        const deliveryInfoObj = this.pincodeData.find((/** @type {{ title: String; pincodes: string; }} */ item) => item.pincodes?.replace(/\s/g, '')?.split(',')?.includes(currentPincode));

        if(deliveryInfoObj?.title && this.variant?.available){
            resultContainer.innerHTML = this.deliveryInfoTemplate.replace('[delivery_info_days]', deliveryInfoObj.title);
            this.storePincode(currentPincode);
            
            //enable atc and buy button when pincode verified 
            this.atcButton?.length && this.atcButton.forEach(el => {
                el.removeAttribute('disabled');
            });
            setTimeout(() => {
                this.stickyAtcButton?.length && this.stickyAtcButton.forEach(el => {
                    el.removeAttribute('disabled');
                });
            }, 500);
            if(this.buyButton) this.buyButton.classList.remove('disabled');
        } else {
            resultContainer.innerHTML = this.responseError;
            this.removePincode();

            //disable atc and buy button when delivery not available for a pincode 
            this.atcButton?.length && this.atcButton.forEach(el => {
                el.setAttribute('disabled', 'disabled');
            });
            setTimeout(() => {
                this.stickyAtcButton?.length && this.stickyAtcButton.forEach(el => {
                    el.setAttribute('disabled', 'disabled');
                });
            }, 500);
            if(this.buyButton) this.buyButton.classList.add('disabled');
        }
        setTimeout(() => {
            this.submitButton?.classList.remove('loading');
        }, 500);
    }
}

if (!customElements.get('pincode-checker')) {
  customElements.define('pincode-checker', PincodeChecker);
}